import type { PublicBuildingState as BuildingState } from "@point/core";

/** Reference extrusion height when using CSS scaleY (non–concept phases only). */
export const BUILDING_H_REF = 46;

/** Concept lots stay short but tall enough to read as iso, not “crushed” by scale. */
const CONCEPT_H_MIN = 24;

export type BuildingMassVariant = 0 | 1 | 2 | 3;

function hashBuildingId(id: string): number {
	let h = 0;
	for (let i = 0; i < id.length; i++) {
		h = (Math.imul(31, h) + id.charCodeAt(i)) | 0;
	}
	return Math.abs(h);
}

function getMassVariant(id: string): BuildingMassVariant {
	return (hashBuildingId(id) % 4) as BuildingMassVariant;
}

/**
 * Footprint + compound-mass layout — shared by iso buildings and plan diagrams
 * so the sheet reads as “this lot,” not a generic template.
 */
export function getIsoFootprint(id: string): {
	variant: BuildingMassVariant;
	footW: number;
	footH: number;
} {
	const variant = getMassVariant(id);
	let footW = 22;
	let footH = 11;
	if (variant === 2) {
		footW = 15;
		footH = 7;
	}
	return { variant, footW, footH };
}

/** Screen-space mass height (matches BlueprintBuilding after variant multipliers). */
export function getBuildingHeightPixels(building: BuildingState): number {
	const variant = getMassVariant(building.app._id);
	let baseH = 16 + building.growthScale * 5.2;
	if (variant === 2) baseH *= 1.28;
	if (building.phase === "new") return Math.max(CONCEPT_H_MIN, baseH * 0.35);
	return baseH;
}

/** True when we must not scale the whole mass in Y — keeps the footprint diamond correct. */
export function hasIntrinsicWireHeight(building: BuildingState): boolean {
	return building.phase === "new";
}

export function getBuildingScaleY(building: BuildingState): number {
	if (hasIntrinsicWireHeight(building)) return 1;
	const hTarget = getBuildingHeightPixels(building);
	return Math.max(0.12, hTarget / BUILDING_H_REF);
}
